import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Panel } from "@/components/AppShell";
import { api, errorText } from "@/lib/api";

function PlayerToggle({ p, out, onToggle, onConfirm, busy }) {
  const doubtful = p.injury && p.injury !== "Out";
  const confirm = (e) => { e.stopPropagation(); if (!busy) onConfirm(p.id, !p.confirmed_playing); };
  return (
    <button type="button" data-testid={`rotation-out-${p.id}`} aria-pressed={out} onClick={onToggle}
      className={`ra-out-row w-full flex items-center gap-3 px-3 py-2 rounded-md border text-left transition-colors ${
        out ? "border-rose-500/70 bg-rose-500/10" : "border-slate-800 hover:border-slate-600"}`}>
      <span className={`h-4 w-4 shrink-0 rounded border ${out ? "bg-rose-500 border-rose-400" : "border-slate-600"}`} />
      <span className="ra-avatar sm">{p.photo && <img src={p.photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</span>
      <span className="flex-1 min-w-0">
        <span className={`block truncate text-sm font-semibold ${out ? "text-rose-200 line-through" : "text-slate-100"}`}>
          {p.jersey && <span className="ra-out-num">#{p.jersey}</span>}{p.name}
        </span>
        <span className="block truncate text-[11px] text-slate-400">
          {p.position || "–"} · {p.games} GP · {p.avg_min} MIN/G · {p.total_min} total
        </span>
      </span>
      {p.starter && <span className="ra-s-badge" title="Confirmed ESPN starter">S</span>}
      {p.confirmed_playing && (
        <span role="button" tabIndex={0} onClick={confirm} data-testid={`rotation-confirmed-${p.id}`}
          title="Επιβεβαιωμένος από εσένα ότι παίζει — πάτα για αναίρεση"
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 text-emerald-300 bg-emerald-500/15 hover:bg-emerald-500/25 cursor-pointer">
          <Check size={11} />Παίζει
        </span>
      )}
      {!p.confirmed_playing && p.injury && (
        <span data-testid={`rotation-injury-${p.id}`} className={`text-[10px] font-bold uppercase rounded px-1 ${p.injury === "Out" ? "text-rose-300 bg-rose-500/15" : "text-amber-300 bg-amber-500/10"}`}>ESPN {p.injury}</span>
      )}
      {!p.confirmed_playing && doubtful && (
        <span role="button" tabIndex={0} onClick={confirm} data-testid={`rotation-confirm-${p.id}`}
          title="Επιβεβαίωσε ότι παίζει — φεύγει η αμφιβολία"
          className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/15 cursor-pointer">
          {busy ? "…" : "Επιβεβαίωση"}
        </span>
      )}
      {out && <span className="text-[10px] font-bold uppercase text-slate-300">{p.material ? "≥10 min" : "<10 min"}</span>}
    </button>
  );
}

export function OutPicker({ ctx, outIds, setOutIds, setCtx }) {
  const [busy, setBusy] = useState(null);
  const toggle = (id) => setOutIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const confirmPlay = async (id, confirmed) => {
    setBusy(id);
    try {
      const payload = { game_id: ctx.game_id, team_id: ctx.team_id, player_id: id };
      const r = confirmed
        ? await api.post("/rotation/confirm-player", payload)
        : await api.delete("/rotation/confirm-player", { params: payload });
      setCtx?.(r.data);
      toast.success(confirmed ? "Επιβεβαιώθηκε ότι παίζει" : "Επαναφορά στην ένδειξη ESPN");
    } catch (e) { toast.error(errorText(e)); }
    setBusy(null);
  };
  const doubtful = ctx.players.filter((p) => (p.injury && p.injury !== "Out") || p.confirmed_playing).length;
  return (
    <Panel className="p-4" data-testid="rotation-out-picker">
      <h3 className="font-display text-xl font-bold uppercase tracking-wide">OUT players · {ctx.team_code}</h3>
      <p className="text-xs text-slate-400 mb-3">
        Pre-selected from the ESPN injury feed (Out). Tap to change. {ctx.starters_confirmed ? "Starters (S) are the confirmed ESPN lineup, read-only." : "No confirmed starters yet."}
        {doubtful > 0 && " Για αμφίβολους παίκτες, πάτα «Επιβεβαίωση» όταν ξέρεις ότι παίζει — φεύγει η ένδειξη."}
      </p>
      <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
        {ctx.players.map((p) => (
          <PlayerToggle key={p.id} p={p} out={outIds.includes(p.id)} onToggle={() => toggle(p.id)}
            onConfirm={confirmPlay} busy={busy === p.id} />
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-3">{ctx.season_label} only · {ctx.sample_games} games before tip-off · {ctx.roster_source}</p>
    </Panel>
  );
}
